import { SendMessageUserGuest } from "@/components/modules/auth/components/SendMessageUserGuest/SendMessageUserGuest";
import { useUserConversations } from "@/components/modules/chat/api/queries/useUserConversations";
import { useConversation } from "@/components/modules/chat/hooks/useConversation";
import { useAuth } from "@/components/providers/AuthProvider/AuthProvider";
import { ModalTypeEnum, useModal } from "@/components/providers/modal-provider/ModalProvider";
import useRealEstateDetail from "@/hooks/QueryHooks/useRealEstateDetail";
import { useToast } from "@/hooks/use-toast";
import { User } from "@/types/me";
import { RealEstateType } from "@/types/real-estate";
import { detailPathForListing } from "../../locationTrail";
import { cleanPrePhone } from "@/utils/phones";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
type Props = {
  realEstateData: RealEstateType;
  realEstateId: string;
};
export function useRealEstateAgencyCard({
  realEstateData,
  realEstateId
}: Props) {
  const t = useTranslations("modules.realEstateDetail");
  const {
    toast
  } = useToast();
  const {
    isAuth,
    userSession: sessionData,
    isPendingAuthData: isPending
  } = useAuth();
  const realEstateDetail = useRealEstateDetail({
    realEstateSlug: realEstateId
  });
  const {
    agents,
    agency,
    realEstate,
    features,
    user
  } = realEstateData;
  const [pendingConversation, setPendingConversation] = useState(false);
  const {
    data: userConversationsData
  } = useUserConversations();
  const {
    handleOpenConversation
  } = useConversation();
  const {
    openModal
  } = useModal();
  const locations = realEstateDetail.data?.data?.locations;
  const isOwner = useMemo(() => user?.id === sessionData?.id, [user, sessionData]);
  const existingConversation = useMemo(() => user ? userConversationsData?.data.find(conversation => conversation?.realEstate?.id === realEstate.id)?.id : null, [user, userConversationsData, sessionData, realEstate]);
  const showUserContacts = realEstateData.user?.showContacts !== false;
  const hasContacts = useMemo(() => {
    if (showUserContacts && realEstateData.user?.phone) {
      return true;
    }
    if (agents?.length > 0 && agents?.some(agent => agent.phone)) {
      return true;
    }
    return false;
  }, [agents, showUserContacts, realEstateData.user]);
  const getFullRouteForDetail = useCallback(() => {
    if (!realEstateId) return;
    const path = detailPathForListing(locations ?? [], realEstateId);
    return `${process.env.NEXT_PUBLIC_SITE_URL}${path}`;
  }, [locations, realEstateId]);
  const url = getFullRouteForDetail();
  const phones = useMemo(() => {
    if (showUserContacts && realEstateData?.user?.phone) {
      return [realEstateData.user.phone];
    }
    const cleanPhones = (input: string | string[] | undefined): string[] => {
      if (!input) return [];
      const arr = Array.isArray(input) ? input : [input];
      return arr.flatMap(phone => phone.split(/[\/|,]/).map(p => cleanPrePhone(p)).filter(Boolean));
    };
    if (agents?.length > 0 && agents?.some(agent_1 => agent_1.phone)) {
      return cleanPhones(agents.map(agent_0 => agent_0.phone));
    } else {
      return cleanPhones(agency?.cellPhones);
    }
  }, [agents, agency, realEstateData.user, showUserContacts]);
  const handleCopy = useCallback((value: string) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(value);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    toast({
      description: t("copiedToClipboard")
    });
  }, []);
  const handleOpenDelayedConversation = useCallback(() => {
    // Instead of running immediately, set pending state
    setPendingConversation(true);
  }, []);
  useEffect(() => {
    if (pendingConversation && sessionData && !isOwner && !isPending) {
      handleOpenConversation({
        realEstateConversationAtributes: {
          image: realEstate?.pictures?.[0],
          title: realEstate?.title
        },
        conversationId: existingConversation,
        realEstateId: realEstate.id,
        recipientId: existingConversation ? undefined : user?.id,
        user: existingConversation ? undefined : user
      });
      setPendingConversation(false);
    }
  }, [pendingConversation, sessionData, handleOpenConversation, existingConversation, realEstateId, user, isOwner, isPending]);
  const handleOpenMessagesAfterLead = ({
    conversationId,
    user: user_0
  }: {
    conversationId: string;
    user: User;
  }) => {
    handleOpenConversation({
      realEstateConversationAtributes: {
        image: realEstate?.pictures?.[0],
        title: realEstate?.title
      },
      conversationId,
      realEstateId: realEstate.id,
      recipientId: user_0?.id,
      user: user_0
    });
  };
  const handleSendMessage = () => {
    if (!isAuth) {
      openModal(ModalTypeEnum.GENERIC_DIALOG, {
        children: <div className="px-4 pb-10">
            <SendMessageUserGuest realEstateId={realEstate.id} recipientId={user?.id} onLoginSuccess={handleOpenMessagesAfterLead} />
          </div>
      });
    } else {
      handleOpenDelayedConversation();
    }
  };
  return {
    handleCopy,
    agency,
    realEstate,
    features,
    phones,
    agents,
    url,
    user,
    hasContacts,
    handleSendMessage,
    isOwner,
    t
  };
}